import dayjs from "dayjs";
import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { BookingCardProdaRow } from "../../../../components/booking-card/BookingCardProdaRow";
import { Text14lh24 } from "../../../../components/text/Text14lh24";
import { useExtractCartItemInfo } from "../../../../hooks/useExtractCartItemInfo";
import { useMoney } from "../../../../hooks/useMoney";
import { useProdaName } from "../../../../hooks/useProdaName";
import { shouldSelectTimeLater } from "../../../../utils/bookingUtils";
import { mergeClass } from "../../../../utils/common";
import { ProdAttrUtils } from "../../../../utils/productAttributeUtils";
import ButtonReceiveTicket from "../../../MyBooking/components/ButtonReceiveTicket";
import ButtonSelectTimeLater from "../../../MyBooking/components/ButtonSelectTimeLater";
import EditOrUpgradeModal from "../MyBooking/EditOrUpgradeModal";
import RefundModal from "../MyBooking/RefundModal";
import { MbrRefundAction } from "./MbrRefundAction";
import { TtIcon } from "./TtIcon";
import { UpgradeRequestSent } from "./UpgradeRequestSent";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { useTranslationEx } from "../../../../hooks/useTranslationEx";

interface MyBookingRowProps {
  onReload?: () => void;
  bookingState?: BookingState;
}
const useMyBookingRow = (props: MyBookingRowProps) => {
  const [showMore, setShowMore] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [modalType, setModalType] = useState("");
  const [selectedBookingDetailIds, setSelectedBookingDetailIds] = useState<
    (number | string)[]
  >([]);

  const handleSelectProduct = (
    productId: number | string,
    checked: boolean,
  ) => {
    if (checked) {
      setSelectedBookingDetailIds([...selectedBookingDetailIds, productId]);
    } else {
      setSelectedBookingDetailIds(
        selectedBookingDetailIds.filter((id) => id !== productId),
      );
    }
  };
  const handleLinkDetail = (productType: TProductType) => {
    if (productType === "TICKET") return "/tickets";
    if (productType === "PASS") return "/pass";
    if (productType === "TOUR") return "/guide-tours";
    if (productType === "MUSICAL") return "/musicals";
    if (productType === "SIM") return "/e-sims";
  };
  const isUpgradeRequestSent =
    !!props.bookingState?.changeRequest?.changeStatus;
  const currentState = props.bookingState?.changeRequest ?? props.bookingState;

  const isCancelRequestSent =
    props.bookingState?.changeRequest?.changeStatus === "REQUEST_CANCEL";
  return {
    showMore,
    setShowMore,
    actionType,
    editModalOpen,
    setEditModalOpen,
    setActionType,
    setSelectedBookingDetailIds,
    modalOpen,
    selectedBookingDetailIds,
    setModalOpen,
    refundModalOpen,
    setRefundModalOpen,
    modalType,
    setModalType,
    handleSelectProduct,
    handleLinkDetail,
    currentState,
    isUpgradeRequestSent,
    isCancelRequestSent,
    ...props,
  };
};

export type TUseMyBookingRow = ReturnType<typeof useMyBookingRow>;

const MyBookingRow: React.FC<MyBookingRowProps> = (props) => {
  const { t } = useTranslation();
  const { formatMoney } = useMoney();
  const jj = useMyBookingRow(props);
  const {
    showMore,
    setShowMore,
    onReload,
    currentState,
    isUpgradeRequestSent,
  } = jj;
  const isMusical = currentState?.product?.productType === "MUSICAL";

  return (
    <>
      <div
        className={
          "rounded-[1.25rem] border border-[#DEDEDE] p-5 " +
          (showMore ? "bg-[#F7F8FA]" : "")
        }
      >
        <div
          className="flex w-full flex-row flex-wrap justify-between gap-4 sm:flex-nowrap sm:gap-0"
          onClick={() => {
            if (!isMusical) setShowMore(!showMore);
          }}
        >
          <div className="flex flex-col items-start gap-2">
            {isUpgradeRequestSent && (
              <UpgradeRequestSent>
                {jj.bookingState?.changeRequest?.changeStatus ===
                "REQUEST_UPDATE"
                  ? jj.bookingState?.changeRequest?.isUpgrade
                    ? t("Upgrade request sent")
                    : t("Edit request sent")
                  : t("Cancel request sent")}
              </UpgradeRequestSent>
            )}

            <div className="flex w-full">
              <TtIcon {...jj} />
              <div className="ml-2 mr-5 flex flex-col space-y-2">
                <div className="flex items-center gap-2">
                  <p className="text-base font-semibold text-[#232323]">
                    {t(currentState?.product?.name ?? "")}
                  </p>
                  {!isMusical && (
                    <button>
                      <img
                        className={`size-4 ${showMore ? "rotate-180" : ""}`}
                        src="../assets/icons/arrow-down.svg"
                        alt={showMore ? "Collapse" : "Expand"}
                      />
                    </button>
                  )}
                </div>
                <TtQty {...jj} />
                <TtInfo {...jj} />
                <span className="text-base font-semibold text-[#232323]">
                  {t("Total paid")}:{" "}
                  {formatMoney(
                    jj.bookingState?.price?.paidAmount ||
                      currentState?.price?.paidAmount,
                  )}
                </span>
                {isUpgradeRequestSent ? (
                  jj.bookingState?.changeRequest?.changeStatus ===
                  "REQUEST_UPDATE" ? (
                    <span className="text-base font-semibold text-[#FF9500]">
                      {t("Additional Paid")}:{" "}
                      {formatMoney(
                        jj.bookingState?.changeRequest?.price?.paidAmount,
                      )}
                    </span>
                  ) : (
                    <span className="text-base font-semibold text-[#FF9500]">
                      {t("Refund Amount")}:{" "}
                      {formatMoney(
                        jj.bookingState?.changeRequest?.price?.refundAmount,
                      )}
                    </span>
                  )
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>

          <div className="ml-auto sm:ml-0">
            <p className="mb-2 text-sm font-normal text-[#232323]">
              {t("Book no")}: {currentState?.bookNo}
            </p>
            <img
              src={currentState?.product?.thumbnail}
              className="h-[8.75rem] w-[12.25rem] rounded-[0.5rem] object-cover"
              alt="" //Add alt attribute for accessibility
            />
          </div>
        </div>
        <TtShowMore {...jj} />
      </div>
      {/* {modalOpen && renderModal()} */}
      {jj.refundModalOpen && (
        <RefundModal
          ids={jj.selectedBookingDetailIds}
          bookingId={currentState?.id!}
          isOpen={jj.refundModalOpen}
          onClose={() => jj.setRefundModalOpen(false)}
          onSubmit={() => {
            jj.setRefundModalOpen(false);
            onReload && onReload();
          }}
        />
      )}
      {jj.editModalOpen && (
        <EditOrUpgradeModal
          productId={Number(currentState?.product?.id)}
          slug={currentState?.product?.slug!}
          bookingId={currentState?.id!}
          productType={currentState?.product?.productType}
          isOpen={jj.editModalOpen}
          productCanEdit={currentState?.childrenLs?.some((x) => x.canEdit)}
          onClose={() => jj.setEditModalOpen(false)}
          onSubmit={() => {
            jj.setEditModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default MyBookingRow;

const TtShowMore = (jj: TUseMyBookingRow) => {
  if (!jj.showMore) return null;
  return (
    <div className="mt-6">
      <aside className="space-y-6">
        <TtItems {...jj} />
      </aside>
      <div>
        <TtActionCard {...jj} />
      </div>
    </div>
  );
};
const TtActionCard = (jj: TUseMyBookingRow) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (jj.actionType === "REFUND") return <MbrRefundAction {...jj} />;
  const disabled = jj.isUpgradeRequestSent;
  const isPass = (jj.currentState?.childrenLs ?? []).length > 1;
  const bookingCannotCancel =
    jj.currentState?.canCancel !== undefined
      ? !jj.currentState?.canCancel
      : jj.currentState?.childrenLs?.every((x) => !x.canCancel);
  const bookingCannotEdit =
    jj.currentState?.canEdit !== undefined
      ? !jj.currentState?.canEdit
      : jj.currentState?.childrenLs?.every((x) => !x.canEdit);
  return (
    <div className="mt-6 flex space-x-4 text-right">
      <button
        disabled={disabled || bookingCannotCancel}
        onClick={() => jj.setActionType("REFUND")}
        className={mergeClass(
          "ml-auto h-[3.125rem] w-full rounded-[3.4375rem] border text-base font-semibold leading-[1.25rem] sm:w-[11.25rem]",
          disabled
            ? "border-[#BFC5C9] text-[#BFC5C9]"
            : "border-[#232323] text-[#232323]",
        )}
      >
        {t("Refund")}
      </button>
      <button
        disabled={disabled || (bookingCannotEdit && !isPass)}
        onClick={() => {
          if (isPass) {
            jj.setActionType("EDIT");
            jj.setEditModalOpen(true);
          } else {
            const urls = [
              { key: "TICKET", url: "/tickets/" },
              { key: "PASS", url: "/pass/" },
              { key: "TOUR", url: "/guide-tours/" },
              { key: "MUSICAL", url: "/musicals/" },
              { key: "SIM", url: "/e-sims/" },
            ];
            const url = urls.find(
              (item) => item.key === jj?.currentState?.product?.productType,
            )?.url;
            navigate(
              `${url}${jj?.currentState?.product?.slug!}?editBookingId=${jj?.currentState?.id}&isEdit=true`,
            );
          }
        }}
        className={mergeClass(
          "ml-auto h-[3.125rem] w-full rounded-[3.4375rem] text-base font-semibold leading-[1.25rem] sm:w-[11.25rem]",
          disabled ? "bg-[#BFC5C9] text-white" : "bg-primary text-[#232323]",
        )}
      >
        {t("Edit")}
      </button>
    </div>
  );
};

const TtItems = (jj: TUseMyBookingRow) => {
  if (!jj.currentState) return null;
  if (jj.currentState?.product?.productType === "PASS") {
    let children = jj.currentState?.childrenLs;
    let isCancelRequest = jj.currentState?.changeStatus === "REQUEST_CANCEL";
    if (isCancelRequest) {
      children = [
        ...(jj.currentState?.childrenLs || []),
        ...(jj.bookingState?.childrenLs ?? []).filter(
          (item2) =>
            !jj.currentState?.childrenLs?.some(
              (item1) => item1?.product?.id === item2.product?.id,
            ),
        ),
      ];
    }
    return (
      <>
        {children?.map((x) => (
          <TtItem key={x.product?.id} jj={jj} state={x} />
        )) ?? null}
      </>
    );
  }
  return <TtItem jj={jj} state={jj.currentState} />;
};

const TtItemProduct = ({
  jj,
  product,
}: {
  jj: TUseMyBookingRow;
  product?: ProductDetailResponse;
}) => {
  const { t2 } = useTranslationEx();
  if (!product) return null;
  return (
    <Link
      to={jj.handleLinkDetail(product.productType) + "/" + product.slug}
      className="text-sm font-semibold text-[#232323]"
    >
      {t2(product.name)}
    </Link>
  );
};

const TtItem: FC<{
  jj: TUseMyBookingRow;
  state: BookingState;
}> = (props) => {
  const { jj, state } = props;
  const { product, bookingDetailId, status } = state;
  const isCancelRequest = jj.currentState?.changeStatus === "REQUEST_CANCEL";

  if (!product) return null;
  return (
    <div
      className="w-full rounded-[1rem] border border-[#DCDCDC] bg-white p-5"
      data-id={bookingDetailId}
    >
      <div className="flex flex-col">
        <div className="flex items-center gap-3">
          <TtCheckBox jj={jj} id={bookingDetailId!} state={state} />
          {jj.handleLinkDetail(product.productType) && (
            <TtItemProduct jj={jj} product={product} />
          )}
        </div>
        {!isCancelRequest &&
          status === "PENDING" &&
          (shouldSelectTimeLater(state) ? (
            <ButtonSelectTimeLater
              booking={state}
              bookingId={state?.id || jj.bookingState?.id}
            />
          ) : (
            <TtBookingDate {...state} />
          ))}
        {status === "CANCEL" && (
          <TtCancelText bookTime={jj.currentState?.bookTime} />
        )}
        {!isCancelRequest && status === "CONFIRMED" && (
          <ButtonReceiveTicket
            bookNo={jj.currentState?.bookNo}
            booking={state}
          />
        )}
      </div>
      <div className="text-right">
        <TtActionItem jj={jj} product={product} />
      </div>
    </div>
  );
};

const TtCancelText = ({ bookTime }: { bookTime?: number }) => {
  const { t } = useTranslation();
  return (
    <Text14lh24 vari="BLACK">
      {t("Cancel request sent") +
        ": " +
        dayjs(new Date((bookTime ?? 0) * 1000)).format("YYYY/MM/DD hh:mm A")}
    </Text14lh24>
  );
};

const TtBookingDate = (state: BookingState) => {
  const { t } = useTranslation();
  const d = ProdAttrUtils.CALENDAR.findDate(state);
  if (!d) return null;
  return <Text14lh24 vari="BLUE">{t("Booking date") + ": " + d}</Text14lh24>;
};

const TtActionItem: FC<{
  jj: TUseMyBookingRow;
  product?: ProductDetailResponse;
}> = ({ jj, product }) => {
  return null;
  // if (!product) return null;

  // const hasSelectedTime =
  //   bookingInfo.priorities && bookingInfo.priorities[0]?.dateTime;

  // if (bookingInfo.status === "pending") {
  //   return !bookingInfo.selectDateNow || !hasSelectedTime
  //     ? renderSelectTimeBtn()
  //     : renderBookingDate();
  // }
  // if (bookingInfo.status === "confirmed") {
  //   return !bookingInfo.selectDateNow || !hasSelectedTime
  //     ? renderSelectTimeBtn()
  //     : renderReceiveTicketBtn();
  // }
  // return null;
};

const TtCheckBox: FC<{
  jj: TUseMyBookingRow;
  id: number | string;
  state: BookingState;
}> = ({ jj, id, state }) => {
  if (jj.actionType !== "REFUND") return null;
  return (
    <input
      disabled={!state?.canCancel}
      type="checkbox"
      checked={jj.selectedBookingDetailIds.includes(id)}
      onChange={(e) => jj.handleSelectProduct(id, e.target.checked)}
      className="custom-checkbox disabled:cursor-not-allowed disabled:opacity-50"
    />
  );
};

const TtQty: FC<TUseMyBookingRow> = (jj) => {
  const currentState = jj.isCancelRequestSent
    ? jj.bookingState
    : jj.currentState;
  const { showAgeLs } = useProdaName();
  const personCount =
    currentState?.bookingAttributes?.find(
      (x) => x?.productAttribute?.name === "PERSON",
    )?.value?.quantity ?? 0;
  if (!currentState) return null;
  return (
    <p className="my-2 text-[0.875rem] font-semibold leading-[1.375rem] text-[#5E6D77]">
      {showAgeLs(currentState)}
      {personCount > 0 ? t("Person") + " x " + personCount : ""}
    </p>
  );
};

const TtInfo: FC<TUseMyBookingRow> = (jj) => {
  const currentState = jj.isCancelRequestSent
    ? jj.bookingState
    : jj.currentState;
  // const hook = useExtractCartItemInfo({ bookingState: currentState });
  const { t2 } = useTranslationEx();
  if (!currentState) return null;
  return (
    <>
      {[
        ...(currentState.childrenLs?.map((x) => t2(x.product?.name)) ?? []),
        // ...(currentState.bookingAttributes?.map((x) => hook.infoText(x)) ?? []),
      ]
        .filter((x) => x)
        .map((x, i) => {
          return (
            <p
              key={i}
              className="my-2 text-[0.875rem] font-normal leading-[1.375rem] text-[#5E6D77]"
            >
              {x}
            </p>
          );
        })}
      <BookingCardProdaRow vari="GRAY" booking={currentState} />
    </>
  );
};
